import { createAction, createReducer } from '@reduxjs/toolkit';
import { TDOId } from '../universal/models/Brands';

/**
 * What the duck records per TDO while a burn-in runs.
 *
 * Exported because three modules used to spell this shape out independently —
 * here, `selectRedactFiles`'s return type, and `casemedia/staleness.ts` — and
 * the third was already describing a stale version of it while still
 * compiling.
 */
export interface RedactFileEntry {
  readonly status: string;
  /**
   * VE-30516: the output version this burn-in was sent to produce — the
   * engine's `assetVersion`. Known at the click, and the only place it exists
   * until the `redaction-user-selections` asset reaches the store, which does
   * not happen until the burn-in finishes.
   */
  readonly version?: number;
  /**
   * VE-30516: when THIS run was started, ISO-8601, set only by the editor path
   * that starts one. Lets the dashboard latch tell a terminal task belonging to
   * a previous run from one belonging to this run — see its release branch.
   */
  readonly startedAt?: string;
}

export type RedactFilesState = Record<string, RedactFileEntry>;

export const REDACT_FILE_COMPLETED = createAction<{
  tdoId: TDOId;
}>('REDACT_FILE_COMPLETED');
export const REDACTING_FILE = createAction<{
  tdoId: TDOId;
  /**
   * Both optional, and both preserved rather than erased when omitted: the
   * callers that merely re-assert "still running" — the dashboard latch, the
   * document and image paths — know neither. Only the editor path that actually
   * starts an engine run supplies them.
   */
  version?: number;
  startedAt?: string;
}>('REDACTING_FILE');
// Eager flag set right when the user clicks REDACT FILE so any UI piece that
// shows "redaction in progress" lights up before the saga has progressed far
// enough to register a running task. Cleared by the redactFile saga.
export const SET_REDACT_JOB_STARTING = createAction<boolean>(
  'SET_REDACT_JOB_STARTING'
);

const defaultState = {
  redactFiles: {} as RedactFilesState,
  isRedactJobStarting: false,
};

const reducer = createReducer(defaultState, (builder) => {
  builder
    .addCase(REDACTING_FILE, (state, action) => ({
      ...state,
      isRedactJobStarting: false,
      redactFiles: {
        ...state.redactFiles,
        [action.payload.tdoId]: {
          status: 'running',
          // Preserved, not erased: the dashboard latch re-asserts "still
          // running" on every fetch and knows neither field, so an omitted one
          // must keep whatever the run that started it established.
          version:
            action.payload.version ??
            state.redactFiles[action.payload.tdoId]?.version,
          startedAt:
            action.payload.startedAt ??
            state.redactFiles[action.payload.tdoId]?.startedAt,
        },
      },
    }))
    .addCase(REDACT_FILE_COMPLETED, (state, action) => ({
      ...state,
      isRedactJobStarting: false,
      redactFiles: {
        ...state.redactFiles,
        [action.payload.tdoId]: {
          status: 'complete',
          // Both dropped deliberately: once the run is over the version
          // describes an output that now exists and can be read from the asset
          // itself, and there is no run left for `startedAt` to date.
        },
      },
    }))
    .addCase(SET_REDACT_JOB_STARTING, (state, action) => ({
      ...state,
      isRedactJobStarting: action.payload,
    }));
});

export default reducer;
export const namespace = 'redactFile';
export const local = (state: { [namespace]: typeof defaultState }) =>
  state[namespace];

export const redactingFile = (
  tdoId: TDOId,
  version?: number,
  startedAt?: string
) => REDACTING_FILE({ tdoId, version, startedAt });

export const redactFileCompleted = (tdoId: TDOId) =>
  REDACT_FILE_COMPLETED({ tdoId });

// Tolerates a store without the slice (component tests that mount a list row
// against a partial mock store) — the real store always registers it.
export const selectRedactFiles = (state: any): RedactFilesState =>
  (local(state) ?? defaultState).redactFiles;

export const selectIsRedactJobStarting = (state: {
  [namespace]: typeof defaultState;
}): boolean => local(state).isRedactJobStarting;
